import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./css/Header.module.css";
import Modal from "./Modal";
import MyProfile from "./MyProfile";
import { CommonContext } from "../../App";
import Text from "../../shared/components/Text";
import Input from "../../shared/components/Input";
import Label from "../../shared/components/Label";
import useDevice from "../../shared/hooks/useDevice";
import IcoDown from "../../assets/img/IcoDown";
import IcoHamburger from "../../assets/img/IcoHamburger";
import IcoSidebarProfile from "../../assets/img/IcoSidebarProfile";
import IcoSidebarLogout from "../../assets/img/IcoSidebarLogout";

type MenuItem = {
    name: string;
    url?: string;
    ico?: string;
    children?: MenuItem[];
};

type ModalState = {
    open: boolean;
    close?: () => void;
    main?: React.ReactNode;
    headerText?: string;
};

const normalizeMenu = (menu: any): MenuItem => ({
    name: menu?.name ?? "",
    url: menu?.url ?? "#",
    ico: menu?.ico,
    children: (menu?.children || []).map((child: any) => normalizeMenu(child)),
});

function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const common = useContext(CommonContext) || {};
    const device = useDevice();
    const menuSource: MenuItem[] = common.myPages || [];
    const safeLogout: () => void = typeof common.logout === "function" ? common.logout : () => {};
    const [menuOpenKey, setMenuOpenKey] = useState<string | null>(null);
    const [sideOpen, setSideOpen] = useState(false);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [myModal, setMyModal] = useState<ModalState>({ open: false });
    const headerRef = useRef<HTMLDivElement | null>(null);

    const menus = useMemo<MenuItem[]>(() => menuSource.map(normalizeMenu), [menuSource]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
                setMenuOpenKey(null);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        setSideOpen(false);
        setMenuOpenKey(null);
        setExpanded({});
    }, [location.pathname]);

    useEffect(() => {
        if (!sideOpen) {
            setExpanded({});
        }
    }, [sideOpen]);

    useEffect(() => {
        if (device === "desktop") {
            setSideOpen(false);
            setExpanded({});
        }
    }, [device]);

    const handleLogoClick = () => {
        if (location.pathname === "/main") {
            window.location.reload();
        } else {
            navigate("/main");
        }
    };

    const isFocused = (menuUrl?: string) => !!menuUrl && location.pathname.startsWith(menuUrl);

    const navigateTo = (menu: MenuItem) => {
        const url = menu.url || "#";
        if (url === "#") return;
        if (location.pathname === url) {
            window.location.reload();
        } else {
            navigate(url);
        }
    };

    const handleOneDepthClick = (menu: MenuItem) => {
        const hasChildren = !!(menu.children && menu.children.length > 0);
        const key = menu.url || menu.name;
        if (hasChildren) {
            setMenuOpenKey((prev) => (prev === key ? null : key));
            return;
        }
        navigateTo(menu);
    };

    const handleTwoDepthClick = (menu: MenuItem) => {
        setMenuOpenKey(null);
        setSideOpen(false);
        navigateTo(menu);
    };

    const closeMyModal = () => {
        setMyModal((prev) => ({ ...prev, open: false }));
    };

    const openMyModal = () => {
        setMyModal({
            open: true,
            close: closeMyModal,
            main: <MyProfile closeMyModal={closeMyModal} />,
            headerText: "내 정보",
        });
    };

    const toggleExpand = (key: string) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <>
            <div className={`${styles.header} ${sideOpen ? styles.open : ""}`} ref={headerRef}>
                <div className={styles.logo} onClick={handleLogoClick}>
                    <img src="/images/logo.png" alt="STN 로고" />
                </div>

                <div className={styles["menu-container"]}>
                    {menus.map((menu) => {
                        const key = menu.url || menu.name;
                        const hasChildren = !!(menu.children && menu.children.length > 0);
                        const open = menuOpenKey === key;
                        return (
                            <div
                                key={key}
                                className={`${styles["one-depth-menu"]} ${
                                    isFocused(menu.url) ? styles["one-depth-menu-focused"] : ""
                                }`}
                                onClick={() => handleOneDepthClick(menu)}
                            >
                                <Text size="lg" weight="semibold">
                                    {menu.name}
                                </Text>
                                {hasChildren && (
                                    <IcoDown className={open ? styles.caretOpen : styles.caret} />
                                )}
                                {hasChildren && (
                                    <div
                                        className={`${styles["menu-dropdown"]} ${
                                            open ? styles["menu-dropdown-open"] : ""
                                        }`}
                                    >
                                        {menu.children!.map((child) => {
                                            const childKey = child.url || child.name;
                                            return (
                                                <div
                                                    key={childKey}
                                                    className={`${styles["two-depth-menu"]} ${
                                                        isFocused(child.url)
                                                            ? styles["two-depth-menu-focused"]
                                                            : ""
                                                    }`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTwoDepthClick(child);
                                                    }}
                                                >
                                                    <Text size="md" weight="regular">
                                                        {child.name}
                                                    </Text>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className={styles["menu-container-right-item"]}>
                    <button className={styles["header-action-btn"]} onClick={openMyModal}>
                        <IcoSidebarProfile />
                        <Text variant="span" size="md" weight="semibold">
                            내정보
                        </Text>
                    </button>
                    <button className={styles["header-action-btn"]} onClick={safeLogout}>
                        <IcoSidebarLogout />
                        <Text variant="span" size="md" weight="semibold">
                            로그아웃
                        </Text>
                    </button>
                </div>
                    <div className={styles["hamburger-wrapper"]} onClick={() => setSideOpen(!sideOpen)}>

                    <IcoHamburger isOpen={sideOpen} />
                    <Input
                        id="hamburger-checkbox"
                        type="checkbox"
                        aria-label="햄버거 메뉴 열기/닫기"
                        hidden
                        checked={sideOpen}
                        onChange={(e) => setSideOpen(e.target.checked)}
                        />
                    </div>
            </div>

            <div
                className={`${styles["side-header-container"]} ${
                    sideOpen ? styles["side-header-container-open"] : ""
                }`}
                onClick={() => setSideOpen(false)}
            >
                <div
                    className={styles["side-header-content"]}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles["side-menu-list"]}>
                        {menus.map((menu) => {
                            const key = menu.url || menu.name;
                            const hasChildren = !!(menu.children && menu.children.length > 0);
                            const isExpanded = !!expanded[key];
                            return (
                                <div
                                    key={key}
                                    className={`${styles["side-mobile-menu-wrapper"]} ${
                                        isExpanded ? styles["side-mobile-menu-wrapper-expanded"] : ""
                                    }`}
                                >
                                    <div
                                        className={`${styles["side-mobile-one-depth-menu"]} ${
                                            isFocused(menu.url) ? styles["side-mobile-menu-focused"] : ""
                                        } ${
                                            isExpanded ? styles["side-mobile-one-depth-menu-expanded"] : ""
                                        }`}
                                        onClick={() => {
                                            if (hasChildren) {
                                                toggleExpand(key);
                                            } else {
                                                setSideOpen(false);
                                                navigateTo(menu);
                                            }
                                        }}
                                    >
                                        <Text size="lg" weight="semibold">
                                            {menu.name}
                                        </Text>
                                        {hasChildren && (
                                            <IcoDown
                                                className={`${styles["side-menu-arrow"]} ${
                                                    isExpanded ? styles["side-menu-arrow-expanded"] : ""
                                                }`}
                                            />
                                        )}
                                    </div>
                                    {hasChildren && isExpanded && (
                                        <div className={styles["side-mobile-submenu-container"]}>
                                            {menu.children!.map((child, idx) => {
                                                const childKey = child.url || `${key}-${idx}`;
                                                const isLast = idx === menu.children!.length - 1;
                                                return (
                                                    <div
                                                        key={childKey}
                                                        className={`${styles["side-mobile-two-depth-menu"]} ${
                                                            isFocused(child.url)
                                                                ? styles["side-mobile-sub-menu-focused"]
                                                                : ""
                                                        } ${
                                                            isLast ? styles["side-mobile-two-depth-menu-last"] : ""
                                                        }`}
                                                        onClick={() => {
                                                            setSideOpen(false);
                                                            navigateTo(child);
                                                        }}
                                                    >
                                                        <Text size="sm" weight="regular" color="rgba(85, 85, 85, 1)">
                                                            {child.name}
                                                        </Text>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles["side-header-bottom"]}>
                        <button
                            className={styles["side-bottom-button"]}
                            onClick={() => {
                                setSideOpen(false);
                                openMyModal();
                            }}
                        >
                            <IcoSidebarProfile />
                            <Text variant="span" size="md" weight={"regular"} color={"#333333"}>
                                내 정보
                            </Text>
                        </button>
                        <button
                            className={styles["side-bottom-button"]}
                            onClick={() => {
                                setSideOpen(false);
                                safeLogout();
                            }}
                        >
                            <IcoSidebarLogout />
                            <Text variant="span" size="md" weight={"regular"} color={"#333333"}>
                                로그아웃
                            </Text>
                        </button>
                    </div>
                </div>
            </div>

            <Modal
                headerText={myModal.headerText}
                size="modal-lg"
                modal={myModal}
                paddingX={24}
                paddingY={24}
            />
        </>
    );
}

export default Header;
